// IPD System Enums
export type IPDPackageType = 'PACKAGE_1' | 'PACKAGE_2' | 'PACKAGE_3';

export enum IPDStatus {
  QUEUED = 'QUEUED',
  ADMITTED = 'ADMITTED',
  DISCHARGED = 'DISCHARGED',
  TRANSFERRED = 'TRANSFERRED'
}

export enum InsuranceType {
  CASHLESS = 'CASHLESS',
  REIMBURSEMENT = 'REIMBURSEMENT',
  NA = 'NA'
}

export enum WardType {
  GENERAL = 'GENERAL',
  ICU = 'ICU',
  PRIVATE = 'PRIVATE',
  SEMI_PRIVATE = 'SEMI_PRIVATE',
  EMERGENCY = 'EMERGENCY'
}

export enum WardSubType {
  AC = 'AC',
  NON_AC = 'NON_AC',
  SINGLE = 'SINGLE',
  DOUBLE = 'DOUBLE',
  TRIPLE = 'TRIPLE',
  QUADRUPLE = 'QUADRUPLE'
}

export enum InsuranceVerificationStatus {
  PENDING = 'PENDING',
  VERIFIED = 'VERIFIED',
  REJECTED = 'REJECTED',
  EXPIRED = 'EXPIRED',
  UNDER_REVIEW = 'UNDER_REVIEW'
}

export enum InsuranceDocumentType {
  POLICY_CARD = 'POLICY_CARD',
  ID_CARD = 'ID_CARD',
  AUTHORIZATION_LETTER = 'AUTHORIZATION_LETTER',
  MEDICAL_REPORTS = 'MEDICAL_REPORTS',
  PRESCRIPTION = 'PRESCRIPTION',
  OTHER = 'OTHER'
}

export enum PatientDocumentCategory {
  MEDICAL_REPORTS = 'MEDICAL_REPORTS',
  LAB_REPORTS = 'LAB_REPORTS',
  IMAGING_REPORTS = 'IMAGING_REPORTS',
  PRESCRIPTION = 'PRESCRIPTION',
  CONSENT_FORMS = 'CONSENT_FORMS',
  DISCHARGE_SUMMARY = 'DISCHARGE_SUMMARY',
  REFERRAL_LETTERS = 'REFERRAL_LETTERS',
  INSURANCE_DOCUMENTS = 'INSURANCE_DOCUMENTS',
  ID_PROOF = 'ID_PROOF',
  EMERGENCY_CONTACT = 'EMERGENCY_CONTACT'
}

// IPD Queue Interfaces
export interface IPDQueueEntry {
  id: string;
  queueNumber: string;
  ipdNumber?: string | null;
  offlineIpdNumber?: string | null;
  status: IPDStatus;
  patient: {
    id: string;
    name: string;
    uhid: string;
    gender: 'MALE' | 'FEMALE' | 'OTHER';
    phone: string;
    dob: string;
    registrationSource?: string;
    registrationSourceDetails?: string | null;
    selfRegistrationPending?: boolean;
    referralPersonName?: string | null;
  };
  createdBy: {
    id: string;
    name: string;
    specialisation: string;
  };
  hospital: {
    id: string;
    name: string;
  };
  createdAt: string;
  updatedAt: string;
  admittedAt?: string;
  dischargedAt?: string;
  admission?: {
    id: string;
    admissionDate: string;
    dischargeDate?: string;
    status: string;
    insuranceType: InsuranceType;
    insuranceCompany: string;
    policyNumber: string;
    tpaName: string;
    wardType: WardType;
    roomNumber: string;
    bedNumber: string;
    chiefComplaint: string;
    admissionNotes: string;
    dischargeNotes?: string;
    totalPackageCost?: number | null;
    surgeryName?: string | null;
    packageType?: IPDPackageType | null;
    gstApplicable?: boolean;
    assignedDoctor: {
      id: string;
      name: string;
      specialisation: string;
    };
    /** Present when a discharge summary record exists for this admission (queue list includes id only). */
    dischargeSummary?: { id: string } | null;
    useOfflineBilling?: boolean;
    offlineSettlementSyncedAt?: string | null;
    thirdPartyHospitalName?: string | null;
  };
}

export interface CreateIPDQueueData {
  patientId: string;
  notes?: string;
}

export interface UpdateIPDQueueStatusData {
  status: IPDStatus;
  notes?: string;
}

// Discharge summary template (saved for reuse)
export interface DischargeSummaryTemplate {
  id: string;
  name: string;
  finalDiagnosis: string;
  treatmentSummary: string;
  proceduresPerformed?: string | null;
  medicationsPrescribed: string;
  followUpInstructions: string;
  followUpReason?: string | null;
  remarks?: string | null;
  doctorSignature?: string | null;
  hospitalStamp?: string | null;
  hospitalId: string;
  createdById?: string | null;
  createdAt: string;
  updatedAt: string;
}

// IPD Admission Interfaces
export interface IPDAdmissionData {
  queueId: string;
  assignedDoctorId: string;
  insuranceType: InsuranceType;
  insuranceCompany?: string;
  policyNumber?: string;
  insuranceNumber?: string;
  tpaName?: string;
  wardType: WardType;
  wardSubType?: WardSubType;
  wardId?: string;
  bedId?: string;
  roomNumber?: string;
  bedNumber?: string;
  chiefComplaint?: string;
  admissionNotes?: string;
  /** Optional referrer name (stored on Patient.referralPersonName) */
  referralPersonName?: string;
  /** Optional partner lead attribution (hospital partnerships). */
  partnerLeadName?: string;
  /** Optional financing partner for third-party bill settlement (e.g. Claimfriendy). */
  financingPartnerName?: string;
  /** External hospital where surgery/stay occurs (no ward/bed at this hospital). */
  thirdPartyHospitalName?: string;
  /** Surgery vs medical management IPD stay. */
  admissionCareType?: 'SURGERY' | 'MEDICAL_MANAGEMENT' | '';
  /** Optional sales person attribution (auto from CRM or manual). */
  salesPersonName?: string;
  /** Lead source at admission (same options as patient registration source). */
  leadSource?: string;
  leadSourceDetails?: string;
  totalPackageCost?: number | string;
  surgeryName?: string;
  surgerySpecialisation?: string;
  /** Multiple package surgeries (specialisation + name). Max 3. */
  packageSurgeries?: Array<{ specialisation?: string | null; surgeryName: string }>;
  packageType?: IPDPackageType;
  /** When checked, 5% GST is applied on the final IPD bill (e.g. plastic surgery). */
  gstApplicable?: boolean;
  /** ISO datetime — only hospital admin / super admin may set on create or edit. */
  admissionDate?: string;
  status?: IPDStatus;
  dischargeDate?: string;
  dischargeNotes?: string;
}

export interface IPDAdmission {
  id: string;
  admissionDate: string;
  dischargeDate?: string;
  status: IPDStatus;
  insuranceType: InsuranceType;
  insuranceCompany?: string;
  policyNumber?: string;
  tpaName?: string;
  wardType: WardType;
  wardSubType?: WardSubType;
  roomNumber?: string;
  bedNumber?: string;
  bedId?: string;
  chiefComplaint?: string;
  admissionNotes?: string;
  dischargeNotes?: string;
  totalPackageCost?: number | null;
  surgeryName?: string | null;
  surgerySpecialisation?: string | null;
  packageSurgeries?: Array<{
    id?: string;
    specialisation?: string | null;
    surgeryName: string;
    sortOrder?: number;
  }>;
  packageType?: IPDPackageType | null;
  gstApplicable?: boolean;
  createdAt: string;
  updatedAt: string;
  queueId: string;
  assignedDoctorId: string;
  insuranceNumber?: string | null;
  salesPersonName?: string | null;
  partnerLeadName?: string | null;
  financingPartnerName?: string | null;
  thirdPartyHospitalName?: string | null;
  admissionCareType?: 'SURGERY' | 'MEDICAL_MANAGEMENT' | null;
  leadSource?: string | null;
  leadSourceDetails?: string | null;
  queue: IPDQueueEntry;
  assignedDoctor: {
    id: string;
    name: string;
    specialisation: string;
  };
  visits?: IPDVisit[];
  dischargeSummary?: IPDDischargeSummary;
  useOfflineBilling?: boolean;
  offlineSettlementSyncedAt?: string | null;
}

// IPD Visit Interfaces
export interface IPDVisitData {
  admissionId: string;
  visitNotes: string;
  clinicalObservations?: string;
  treatmentGiven?: string;
  medicationChanges?: string;
  patientResponse?: string;
  nextVisitPlan?: string;
  vitals?: IPDVisitVitalData[];
}

export interface IPDVisitVitalData {
  type: 'BP_SYSTOLIC' | 'BP_DIASTOLIC' | 'HEART_RATE' | 'TEMPERATURE' | 'WEIGHT' | 'HEIGHT' | 'SPO2' | 'RESPIRATORY_RATE';
  value: number;
  unit?: string;
  notes?: string;
}

export interface IPDVisit {
  id: string;
  admissionId: string;
  doctorId: string;
  visitNotes: string;
  clinicalObservations?: string;
  treatmentGiven?: string;
  medicationChanges?: string;
  patientResponse?: string;
  nextVisitPlan?: string;
  visitDate?: string; // Primary date field from database
  createdAt?: string; // Fallback field
  vitals?: IPDVisitVital[];
  doctor: {
    id: string;
    name: string;
    specialisation: string;
  };
}

export interface IPDVisitVital {
  id: string;
  type: 'BP_SYSTOLIC' | 'BP_DIASTOLIC' | 'HEART_RATE' | 'TEMPERATURE' | 'WEIGHT' | 'HEIGHT' | 'SPO2' | 'RESPIRATORY_RATE';
  value: number;
  unit?: string;
  notes?: string;
  recordedAt: string;
}

// IPD Discharge Summary Interface
export interface IPDDischargeSummary {
  id: string;
  summaryDate: string;
  admissionDate: string;
  dischargeDate: string;
  totalStayDuration: number;
  chiefComplaint: string;
  finalDiagnosis: string;
  treatmentSummary: string;
  proceduresPerformed?: string;
  medicationsPrescribed: string;
  followUpInstructions: string;
  followUpAt?: string | null;
  followUpReason?: string | null;
  remarks?: string;
  doctorSignature?: string;
  hospitalStamp?: string;
  createdAt: string;
  updatedAt: string;
}

/** Discharged patient with a scheduled IPD follow-up (list / dashboard). */
export interface IPDDischargeFollowUpItem {
  id: string;
  admissionId: string;
  followUpAt: string | null;
  followUpReason: string | null;
  followUpInstructions: string;
  dischargeDate: string;
  admission: {
    id: string;
    dischargeDate: string | null;
    queue: {
      ipdNumber: string | null;
      patient: {
        id: string;
        name: string;
        phone: string;
        uhid: string;
      };
    };
    assignedDoctor: {
      id: string;
      name: string;
      specialisation: string | null;
    };
  };
}

// Ward Management Interfaces
export interface Ward {
  id: string;
  name: string;
  type: WardType;
  subType?: WardSubType;
  totalBeds: number;
  occupiedBeds: number;
  availableBeds: number;
  pricePerDay?: number;
  description?: string;
  hospitalId: string;
  createdAt: string;
  updatedAt: string;
}

export interface Bed {
  id: string;
  bedNumber: string;
  isOccupied: boolean;
  pricePerDay?: number;
  wardId: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateWardData {
  name: string;
  type: WardType;
  subType?: WardSubType;
  totalBeds: number;
  pricePerDay?: number;
  description?: string;
}

export interface UpdateWardData {
  name?: string;
  type?: WardType;
  subType?: WardSubType;
  pricePerDay?: number;
  description?: string;
}

export interface UpdateWardBedCountData {
  occupiedBeds: number;
  availableBeds: number;
}

// Insurance Company Interfaces
export interface InsuranceCompany {
  id: string;
  name: string;
  isPartnered: boolean;
  tpaName?: string;
  contactInfo?: string;
  hospitalId: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateInsuranceCompanyData {
  name: string;
  isPartnered?: boolean;
  tpaName?: string;
  contactInfo?: string;
}

// Insurance Processing Interfaces
export interface InsuranceVerification {
  id: string;
  verificationStatus: InsuranceVerificationStatus;
  policyValidFrom?: string;
  policyValidTo?: string;
  coverageAmount?: number;
  deductibleAmount?: number;
  coPaymentPercentage?: number;
  preAuthorizationRequired: boolean;
  preAuthorizationNumber?: string;
  preAuthorizationDate?: string;
  preAuthorizationExpiry?: string;
  admissionId: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateInsuranceVerificationData {
  admissionId: string;
  policyValidFrom?: string;
  policyValidTo?: string;
  coverageAmount?: number;
  deductibleAmount?: number;
  coPaymentPercentage?: number;
  preAuthorizationRequired: boolean;
  preAuthorizationNumber?: string;
  preAuthorizationDate?: string;
  preAuthorizationExpiry?: string;
}

export interface InsuranceDocument {
  id: string;
  fileName: string;
  fileUrl: string;
  fileSize: number;
  mimeType: string;
  documentType: InsuranceDocumentType;
  admissionId: string;
  uploadedById: string;
  createdAt: string;
}

export interface CreateInsuranceDocumentData {
  admissionId: string;
  uploadedById: string;
  fileName: string;
  fileUrl: string;
  fileSize: number;
  mimeType: string;
  documentType: InsuranceDocumentType;
}

// Dashboard Statistics Interface
export interface IPDDashboardStats {
  totalQueued: number;
  totalAdmitted: number;
  totalDischarged: number;
  wardOccupancy: Array<{
    name: string;
    type: WardType;
    totalBeds: number;
    occupiedBeds: number;
    availableBeds: number;
  }>;
}

// Doctor Interface (shared with other modules)
export interface Doctor {
  id: string;
  name: string;
  specialisation: string;
  status: 'ACTIVE' | 'INACTIVE';
}

// API Response Types
export interface IPDQueueResponse {
  message: string;
  data: IPDQueueEntry;
}

export interface IPDAdmissionResponse {
  message: string;
  data: IPDAdmission;
}

export interface IPDDashboardStatsResponse {
  message: string;
  data: IPDDashboardStats;
}

export interface WardsResponse {
  message: string;
  data: Ward[];
}

// IPD Patient Document Interfaces
export interface IPDPatientDocument {
  id: string;
  admissionId: string;
  uploadedById: string;
  fileName: string;
  fileUrl: string;
  fileSize: number;
  mimeType: string;
  category: PatientDocumentCategory;
  description?: string;
  uploadedBy: {
    id: string;
    name: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface UploadIPDPatientDocumentData {
  admissionId: string;
  category: PatientDocumentCategory;
  description?: string;
  file: File;
}

export interface InsuranceCompaniesResponse {
  message: string;
  data: InsuranceCompany[];
}

export interface DoctorsResponse {
  message: string;
  data: Doctor[];
}
